import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { FileStack, AlertCircle, AlertTriangle, ArrowUp, ArrowDown, Pencil, Plus, Search, X, Check, Loader2, Users, ListChecks } from 'lucide-react';
import { ctaFinalApprovalApi, requestsApi, requestTypeSettingsApi } from '../../lib/api';
import { isImageFilename } from '../../lib/utils';
import EsignModeTabs from '../EsignModeTabs';
import { ReviewStagesEditor, DEFAULT_REVIEW_STAGES, stageDisplay, stagesFromRequest } from '../ReviewStagesEditor';
import { toast } from 'sonner';

const ROLE_LABELS = {
  EXEC_DIRECTOR: 'Executive Director',
  DIRECTOR: 'Director',
  CHAIRMAN: 'Chairman',
  FINANCE: 'Finance',
  FINANCIAL_OFFICER: 'Financial Officer',
  COORDINATOR: 'Coordinator',
  CRD: 'Admin Support',
  ADMIN: 'Administrator',
  COMPANY_REP: 'Company Representative',
  INVESTIGATOR: 'Investigator',
  REVIEWER: 'Reviewer',
};

const roleDisplay = (sig) => sig.display_label_override || ROLE_LABELS[sig.signature_role] || sig.signature_role;

/**
 * FT Chairman final approval + e-signature bundle configuration.
 * The Chairman checks documents → the backend auto-generates ONE combined PDF
 * (request form as cover pages + every checked doc appended) which is what the
 * e-signature cascade signs. A single signatory list applies to the bundle.
 */
export default function FtChairmanApprovalModal({ open, onOpenChange, request, editMode = false, roundMode = false, onSuccess, onSwitchMode }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [docs, setDocs] = useState([]);
  const [checked, setChecked] = useState({});
  // Per-bundle display-name overrides — Chairman can rename a doc just for THIS bundle without
  // touching the source filename in db.documents. Keyed by document_id.
  const [displayNames, setDisplayNames] = useState({});
  const [renamingDocId, setRenamingDocId] = useState(null);
  const [signatories, setSignatories] = useState([]);
  const [signingMode, setSigningMode] = useState('default');
  // Phase 3: re-sign-new-version admin toggle (round mode only)
  const [allowResign, setAllowResign] = useState(true);
  // Add-signatory picker
  const [pickerOpen, setPickerOpen] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState({ involved: [], others: [] });
  const [pickerSearch, setPickerSearch] = useState('');
  // Confirmation when picking a user outside every bundled document's thread
  const [pendingAdd, setPendingAdd] = useState(null);
  // Inline label override editing
  const [editingLabel, setEditingLabel] = useState({ idx: null, value: '' });
  // Phase 5: Chairman-configured ordered review flow (runs BEFORE e-signature collection).
  // 2-step wizard: Step 1 = review stages (skippable), Step 2 = e-signature configuration.
  const [reviewStages, setReviewStages] = useState([]);
  const [step, setStep] = useState(1);
  const showWizard = !editMode; // create + round modes get Step 1; edit stays signatures-only

  useEffect(() => {
    if (open && request?.id) loadData();
    if (open && !editMode) {
      setStep(1);
      setReviewStages(roundMode
        ? stagesFromRequest(request?.review_stages)
        : [...(DEFAULT_REVIEW_STAGES[request?.type === 'CTA' ? 'CTA' : 'FT'] || [])]);
    } else if (open) {
      setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, request?.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const calls = [
        ctaFinalApprovalApi.getSignableDocuments(request.id),
        ctaFinalApprovalApi.getAvailableSignatories(request.id),
      ];
      if (editMode) calls.push(ctaFinalApprovalApi.getSignatureConfig(request.id));
      const responses = await Promise.all(calls);
      const docList = responses[0].data || [];
      setDocs(docList);

      if (roundMode) {
        try {
          const rs = await requestTypeSettingsApi.getSignatureRoundsConfig(request?.type || 'FT');
          setAllowResign(rs.data?.allow_resign_new_version !== false);
        } catch { /* keep default */ }
      }

      // Pre-check docs in edit mode from the saved bundle sources, preserving the saved order
      const initChecked = {};
      if (editMode) {
        (request.ft_bundle_source_document_ids || []).forEach((id, idx) => { initChecked[id] = idx + 1; });
      }
      setChecked(initChecked);
      // Restore saved display-name overrides (rename-for-bundle)
      setDisplayNames(editMode ? { ...(request.ft_bundle_source_display_names || {}) } : {});

      // Signatories: edit mode → saved config of the CURRENT bundle; create/round mode → institutional defaults
      const cfgList = editMode ? (responses[2]?.data?.configs || []) : [];
      const savedCfg = editMode
        ? (cfgList.find(c => c.document_id === request.ft_bundle_document_id) || cfgList[0])
        : null;
      if (savedCfg && savedCfg.signatories?.length) {
        setSignatories(savedCfg.signatories.map((s, idx) => ({
          user_id: s.user_id,
          user_name: s.user_name,
          user_role: s.user_role,
          signature_role: s.signature_role,
          order: s.order ?? (idx + 1),
          enabled: true,
          removable: true,
          display_label_override: s.display_label_override || null,
        })));
        setSigningMode(savedCfg.signing_mode || 'default');
      } else {
        const data = responses[1].data || {};
        const defaults = [];
        let order = 1;
        if (data.exec_director) {
          defaults.push({ ...data.exec_director, signature_role: 'EXEC_DIRECTOR', order: order++, enabled: true, removable: false, display_label_override: null });
        }
        if (data.director) {
          defaults.push({ ...data.director, signature_role: 'DIRECTOR', order: order++, enabled: true, removable: false, display_label_override: null });
        }
        // iter167 Issue 26 — FT default signatories. Per user spec the
        // 4-slot default order is: Exec Director → Director → FINANCE →
        // Financial Officer. All four are auto-populated and ENABLED by
        // default (Chairman can still reorder / add / remove). FINANCE
        // is the single first active Finance user (no longer a flat
        // list — multiple Finance users would clutter the dialog).
        if ((data.finance || []).length > 0) {
          const fin = data.finance[0];
          defaults.push({ ...fin, signature_role: 'FINANCE', order: order++, enabled: true, removable: false, display_label_override: null });
        }
        if (data.financial_officer) {
          defaults.push({ ...data.financial_officer, signature_role: 'FINANCIAL_OFFICER', order: order++, enabled: true, removable: false, display_label_override: null });
        }
        // CTA bundle mode (Phase 4): mirror the CTA per-document defaults — company rep +
        // PIs enabled, Co-Is listed but unchecked (Chairman opts in)
        if (request?.type === 'CTA') {
          if (data.company_rep) {
            defaults.push({ ...data.company_rep, signature_role: 'COMPANY_REP', order: order++, enabled: true, removable: true, display_label_override: null });
          }
          (data.pis || []).forEach((p) => {
            defaults.push({ ...p, signature_role: p.signature_role || 'PI', order: order++, enabled: true, removable: true, display_label_override: null });
          });
          (data.co_investigators || []).forEach((c) => {
            defaults.push({ ...c, signature_role: c.signature_role || 'CO_PI', order: order++, enabled: false, removable: true, display_label_override: null });
          });
        }
        setSignatories(defaults);
        setSigningMode('default');
      }
    } catch (e) {
      console.error('Failed to load FT e-sign data:', e);
      toast.error('Failed to load documents and signatories');
    } finally {
      setLoading(false);
    }
  };

  // `checked` is now an order map: { docId: 1-based position } (truthy = included, value = bundle order).
  // Selecting a doc appends at the end; unselecting re-sequences the remaining ones.
  const toggleDoc = (docId) => setChecked(prev => {
    if (prev[docId]) {
      // Remove and re-sequence the remaining checks so positions stay 1..N
      const next = { ...prev };
      delete next[docId];
      const ordered = Object.entries(next).sort((a, b) => a[1] - b[1]);
      const compacted = {};
      ordered.forEach(([id], idx) => { compacted[id] = idx + 1; });
      return compacted;
    }
    const nextOrder = Object.keys(prev).length + 1;
    return { ...prev, [docId]: nextOrder };
  });

  // Swap the position of `docId` with the adjacent ordered doc (dir = -1 up, +1 down).
  // No-op at the boundaries.
  const moveDoc = (docId, dir) => setChecked(prev => {
    const ordered = Object.entries(prev).sort((a, b) => a[1] - b[1]);
    const idx = ordered.findIndex(([id]) => id === docId);
    const swapIdx = idx + dir;
    if (idx < 0 || swapIdx < 0 || swapIdx >= ordered.length) return prev;
    const [a, b] = [ordered[idx], ordered[swapIdx]];
    return { ...prev, [a[0]]: b[1], [b[0]]: a[1] };
  });

  // Why a document cannot be selected for a new round — null when selectable.
  const getDocLockReason = (doc) => {
    if (!doc) return null;
    // iter167 Issue 9 — Always show the lock badge on already-signed docs in
    // ALL FT setup modes (Chairman final approval AND new signature round)
    // so the FT Chairman can never accidentally re-route a signed doc
    // through a fresh round. Mirrors the CTA modal fix.
    if (doc.already_signed) {
      if (allowResign && roundMode) return 'Re-signing previously signed documents requires opening a NEW round explicitly';
      return 'Already signed in a previous round';
    }
    if (!roundMode || allowResign) return null;
    if (doc.prev_version_signed) return 'A previous version was already signed — re-signing is disabled in Admin Config';
    return null;
  };

  const toggleSignatory = (idx) => setSignatories(prev => prev.map((s, i) => i === idx ? { ...s, enabled: !s.enabled } : s));
  const removeSignatory = (idx) => setSignatories(prev => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })));
  const moveSignatory = (idx, dir) => setSignatories(prev => {
    const arr = [...prev];
    const ni = dir === 'up' ? idx - 1 : idx + 1;
    if (ni < 0 || ni >= arr.length) return prev;
    [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
    return arr.map((s, i) => ({ ...s, order: i + 1 }));
  });
  const updateLabelOverride = (idx, value) => setSignatories(prev => prev.map((s, i) =>
    i === idx ? { ...s, display_label_override: value && value.trim() ? value.trim() : null } : s
  ));

  const openPicker = async () => {
    setPickerOpen(true);
    setPickerSearch('');
    if (eligibleUsers.involved.length === 0 && eligibleUsers.others.length === 0) {
      try {
        const res = await ctaFinalApprovalApi.getEligibleSignatoryUsers(request.id);
        setEligibleUsers(res.data);
      } catch (e) {
        console.error('Failed to load eligible signatory users', e);
        toast.error('Could not load user list');
      }
    }
  };

  const addSignatory = (picked) => {
    setSignatories(prev => {
      if (prev.some(s => s.user_id === picked.user_id)) return prev;
      return [...prev, {
        user_id: picked.user_id,
        user_name: picked.user_name,
        user_role: picked.user_role,
        signature_role: picked.user_role,
        order: prev.length + 1,
        enabled: true,
        removable: true,
        display_label_override: null,
      }];
    });
    setPickerOpen(false);
    setPickerSearch('');
  };

  // Thread-membership intelligence: a user is "in the bundle threads" when they are a
  // member of at least one CHECKED document's discussion thread.
  const isUserInBundleThreads = (userId) => {
    const checkedDocs = docs.filter(d => checked[d.id]);
    if (checkedDocs.length === 0) return true; // nothing checked yet — don't warn
    return checkedDocs.some(d => (d.thread_member_ids || []).includes(userId));
  };

  const pickSignatory = (picked) => {
    if (!isUserInBundleThreads(picked.user_id)) {
      setPendingAdd(picked);
      return;
    }
    addSignatory(picked);
  };

  const filterUsers = (list) => {
    const q = pickerSearch.trim().toLowerCase();
    const alreadyIds = new Set(signatories.map(s => s.user_id));
    return (list || [])
      .filter(u => !alreadyIds.has(u.user_id))
      .filter(u => !q || `${u.user_name} ${u.email || ''} ${u.user_role}`.toLowerCase().includes(q));
  };

  const handleSubmit = async () => {
    // Order matters — the bundle PDF is built in the same order the chairman picks here.
    const document_ids = Object.entries(checked)
      .sort((a, b) => a[1] - b[1])
      .map(([id]) => id);
    if (document_ids.length === 0) {
      toast.error('Please check at least one document for the bundle');
      return;
    }
    const enabledSigs = signatories.filter(s => s.enabled).map((s, idx) => ({
      user_id: s.user_id,
      user_name: s.user_name,
      user_role: s.user_role,
      signature_role: s.signature_role,
      order: idx + 1,
      display_label_override: s.display_label_override || null,
    }));
    if (enabledSigs.length === 0) {
      toast.error('At least one signatory is required');
      return;
    }
    // Only forward display-name overrides for the docs that are actually in the bundle.
    const bundle_display_names = {};
    document_ids.forEach(id => {
      const name = (displayNames[id] || '').trim();
      if (name) bundle_display_names[id] = name;
    });
    const payload = { document_ids, signatories: enabledSigs, signing_mode: signingMode, bundle_display_names };
    // Phase 5: review stages apply at approval AND round time (not edit)
    const stagesPayload = reviewStages.map(s => (s.user_id ? { user_id: s.user_id } : { role: s.role }));
    setSubmitting(true);
    try {
      if (roundMode) {
        const res = await requestsApi.startSignatureRound(request.id, { ...payload, review_stages: stagesPayload });
        toast.success(res.data?.message || 'New signature round started');
      } else if (editMode) {
        const res = await requestsApi.ftEsignEdit(request.id, payload);
        toast.success(res.data?.message || 'E-signature configuration updated');
      } else if (request?.type === 'CTA') {
        // Phase 4: CTA in bundle mode goes through cta-final-approve with mode=bundle
        const res = await ctaFinalApprovalApi.submitFinalApprovalBundle(request.id, { ...payload, review_stages: stagesPayload });
        toast.success(res.data?.message || 'Final approval submitted — e-signature bundle generated');
      } else {
        await requestsApi.ftChairmanApprove(request.id, { ...payload, review_stages: stagesPayload });
        toast.success('Approved — e-signature bundle generated');
      }
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      console.error('FT e-sign submit failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const checkedCount = docs.filter(d => checked[d.id]).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ft-esign-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileStack className="w-5 h-5" />
            {roundMode ? 'Start New Signature Round' : editMode ? 'Edit E-Signature Configuration' : 'Approve & Configure E-Signature'}
          </DialogTitle>
          <DialogDescription>
            {roundMode
              ? 'Check the newly approved documents for an additional signature round. A fresh bundle PDF is generated for this round — previously signed bundles stay untouched.'
              : editMode
              ? 'Adjust the bundled documents or signatories. The bundle PDF is regenerated only if the document list changes. Allowed only before the first signature is collected.'
              : 'Check the documents to include in the auto-generated bundle and configure the signatories.'}
          </DialogDescription>
        </DialogHeader>

        {/* Phase 4: signing-mode selector — switching swaps to the per-document modal */}
        {onSwitchMode && !editMode && step === 2 && (
          <EsignModeTabs mode="bundle" onSelect={(m) => { if (m === 'per_document') onSwitchMode(); }} />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
          </div>
        ) : showWizard && step === 1 ? (
          /* Step 1 of 2 — Review Stages (skippable) */
          <div className="space-y-4" data-testid="esign-wizard-step1">
            <p className="text-sm text-slate-600">
              <span className="font-semibold">Step 1 of 2.</span> Define who reviews this request — in
              order — before e-signatures are collected. Add roles or specific people, or skip to go
              straight to signatures.
            </p>
            <ReviewStagesEditor
              stages={reviewStages}
              onChange={setReviewStages}
              requestType={request?.type === 'CTA' ? 'CTA' : 'FT'}
              requestId={request?.id}
            />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">One combined PDF will be auto-generated</p>
                <p className="mt-1">The {request?.type === 'CTA' ? 'CTA' : 'FT'} request form becomes the cover pages, followed by every checked document. This single bundle is what goes through the e-signature cascade.</p>
              </div>
            </div>

            {/* Document checklist — chairman picks AND orders the docs that go into the bundle PDF */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Documents to include in the bundle (approved PDF, Word, or image):</Label>
              <p className="text-[11px] text-slate-500 -mt-1">Bundle PDF pages appear in the order shown below. Use the arrows to re-order.</p>
              {docs.length === 0 ? (
                <p className="text-sm text-slate-500 italic p-3 text-center border rounded-lg">
                  No approved PDF, Word, or image documents available. Approve documents before final approval.
                </p>
              ) : (() => {
                const orderedCheckedDocs = Object.entries(checked)
                  .sort((a, b) => a[1] - b[1])
                  .map(([id]) => docs.find(d => d.id === id))
                  .filter(Boolean);
                const uncheckedDocs = docs.filter(d => !checked[d.id]);
                const renderRow = (doc, isOrdered, position, total) => {
                  const lockReason = getDocLockReason(doc);
                  const docIsImage = isImageFilename(doc.filename);
                  const renamed = (displayNames[doc.id] || '').trim();
                  const isRenaming = renamingDocId === doc.id;
                  return (
                    <Card key={doc.id} className={`transition-colors ${lockReason ? 'opacity-70 bg-slate-50' : isOrdered ? 'border-emerald-500 bg-emerald-50/30' : ''}`} data-testid={`ft-esign-doc-row-${doc.id}`}>
                      <CardContent className="p-3 flex items-start gap-3">
                        <Checkbox
                          id={`ft-doc-${doc.id}`}
                          checked={lockReason ? false : !!checked[doc.id]}
                          onCheckedChange={() => !lockReason && toggleDoc(doc.id)}
                          disabled={!!lockReason}
                          data-testid={`ft-esign-doc-checkbox-${doc.id}`}
                          className="mt-0.5"
                        />
                        {isOrdered && (
                          <div className="flex flex-col items-center gap-0.5 mt-0.5">
                            <button
                              type="button"
                              onClick={() => moveDoc(doc.id, -1)}
                              disabled={position === 1}
                              data-testid={`ft-doc-move-up-${doc.id}`}
                              className="p-0.5 rounded hover:bg-emerald-100 disabled:opacity-30 disabled:cursor-not-allowed"
                              aria-label="Move up"
                            >
                              <ArrowUp className="w-3.5 h-3.5 text-emerald-700" />
                            </button>
                            <span className="text-[10px] font-semibold text-emerald-800 w-5 text-center" data-testid={`ft-doc-order-${doc.id}`}>
                              {position}
                            </span>
                            <button
                              type="button"
                              onClick={() => moveDoc(doc.id, 1)}
                              disabled={position === total}
                              data-testid={`ft-doc-move-down-${doc.id}`}
                              className="p-0.5 rounded hover:bg-emerald-100 disabled:opacity-30 disabled:cursor-not-allowed"
                              aria-label="Move down"
                            >
                              <ArrowDown className="w-3.5 h-3.5 text-emerald-700" />
                            </button>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <label htmlFor={`ft-doc-${doc.id}`} className="cursor-pointer flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm truncate">
                              {renamed ? (
                                <>
                                  {renamed}
                                  <span className="text-[11px] text-slate-400 ml-1.5" data-testid={`ft-doc-source-${doc.id}`}>(source: {doc.filename})</span>
                                </>
                              ) : (
                                doc.filename
                              )}
                            </span>
                            <Badge variant="outline" className="text-xs">{doc.doc_type}</Badge>
                            <Badge variant="secondary" className="text-xs">v{doc.version}</Badge>
                            {docIsImage && !lockReason && (
                              <Badge className="text-xs bg-indigo-100 text-indigo-700 border-indigo-200" data-testid={`ft-doc-image-badge-${doc.id}`}>
                                Image &rarr; PDF
                              </Badge>
                            )}
                            {renamed && !lockReason && (
                              <Badge className="text-xs bg-sky-100 text-sky-700 border-sky-200" data-testid={`ft-doc-renamed-badge-${doc.id}`}>
                                Renamed for bundle
                              </Badge>
                            )}
                            {lockReason && (
                              <Badge variant="outline" className="text-xs bg-slate-100 text-slate-600 border-slate-300" data-testid={`ft-doc-lock-badge-${doc.id}`}>
                                {lockReason}
                              </Badge>
                            )}
                            {!lockReason && roundMode && (doc.already_signed || doc.prev_version_signed) && (
                              <Badge className="text-xs bg-amber-100 text-amber-800" data-testid={`ft-doc-resign-badge-${doc.id}`}>
                                Re-sign
                              </Badge>
                            )}
                            {/* Pencil → only meaningful when the doc is included; show on every row but disabled when not */}
                            {!lockReason && isOrdered && (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); setRenamingDocId(isRenaming ? null : doc.id); }}
                                className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] border border-slate-200 text-slate-600 hover:bg-slate-100"
                                data-testid={`ft-doc-rename-btn-${doc.id}`}
                                title={renamed ? 'Edit bundle display name' : 'Rename for this bundle'}
                              >
                                <Pencil className="w-3 h-3" />
                                {renamed ? 'Edit name' : 'Rename'}
                              </button>
                            )}
                          </label>
                          {isOrdered && isRenaming && (
                            <div className="mt-2 flex items-center gap-2" data-testid={`ft-doc-rename-row-${doc.id}`}>
                              <Input
                                autoFocus
                                value={displayNames[doc.id] ?? ''}
                                onChange={(e) => setDisplayNames(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                placeholder={`Display name in bundle (default: ${doc.filename})`}
                                className="h-8 text-sm"
                                data-testid={`ft-doc-rename-input-${doc.id}`}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') { e.preventDefault(); setRenamingDocId(null); }
                                  if (e.key === 'Escape') { e.preventDefault(); setRenamingDocId(null); }
                                }}
                              />
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-8"
                                onClick={() => setRenamingDocId(null)}
                                data-testid={`ft-doc-rename-done-${doc.id}`}
                              >Done</Button>
                              {(displayNames[doc.id] || '').trim() && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 text-xs text-slate-500 hover:text-red-600"
                                  onClick={() => setDisplayNames(prev => {
                                    const next = { ...prev };
                                    delete next[doc.id];
                                    return next;
                                  })}
                                  data-testid={`ft-doc-rename-clear-${doc.id}`}
                                >Clear</Button>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                };
                return (
                  <>
                    {orderedCheckedDocs.length > 0 && (
                      <div className="space-y-2" data-testid="ft-esign-bundle-ordered-list">
                        <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-semibold">In bundle ({orderedCheckedDocs.length}) — order matters</div>
                        {orderedCheckedDocs.map((doc, idx) => renderRow(doc, true, idx + 1, orderedCheckedDocs.length))}
                      </div>
                    )}
                    {uncheckedDocs.length > 0 && (
                      <div className="space-y-2 mt-3" data-testid="ft-esign-bundle-available-list">
                        <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Available ({uncheckedDocs.length})</div>
                        {uncheckedDocs.map(doc => renderRow(doc, false))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Signatory configuration (single list for the bundle) */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  <Users className="w-4 h-4" />
                  Bundle Signatories ({signatories.filter(s => s.enabled).length})
                </Label>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Signing Order:</Label>
                  <Select value={signingMode} onValueChange={setSigningMode}>
                    <SelectTrigger className="w-48 h-8" data-testid="ft-esign-signing-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default (Exec → Dir → Others)</SelectItem>
                      <SelectItem value="sequential">Fully Sequential</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {signingMode === 'sequential' && (
                <p className="text-xs text-slate-500">Use the arrows to reorder signers — they will sign in this exact order.</p>
              )}

              {signatories.map((sig, idx) => {
                const isEditingThis = editingLabel.idx === idx;
                return (
                  <div
                    key={`${sig.user_id}-${idx}`}
                    className={`flex items-center gap-3 p-2 rounded border ${sig.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                    data-testid={`ft-esign-signatory-row-${idx}`}
                  >
                    {signingMode === 'sequential' && (
                      <div className="flex flex-col gap-0.5">
                        <button type="button" className="text-slate-500 hover:text-blue-700 disabled:text-slate-300" onClick={() => moveSignatory(idx, 'up')} disabled={idx === 0} data-testid={`ft-esign-signatory-up-${idx}`}>
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button type="button" className="text-slate-500 hover:text-blue-700 disabled:text-slate-300" onClick={() => moveSignatory(idx, 'down')} disabled={idx === signatories.length - 1} data-testid={`ft-esign-signatory-down-${idx}`}>
                          <ArrowDown className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <Checkbox checked={sig.enabled} onCheckedChange={() => toggleSignatory(idx)} data-testid={`ft-esign-signatory-enable-${idx}`} />
                    <span className="text-sm font-medium w-6">{idx + 1}.</span>
                    <div className="flex-1 flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-sm font-medium truncate">{sig.user_name}</span>
                      {!isUserInBundleThreads(sig.user_id) && (
                        <span title="Not a member of any bundled document's discussion thread">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500" data-testid={`ft-esign-thread-warning-${idx}`} />
                        </span>
                      )}
                      {isEditingThis ? (
                        <div className="flex items-center gap-1">
                          <Input
                            autoFocus
                            className="h-7 text-xs w-[180px]"
                            value={editingLabel.value}
                            onChange={(e) => setEditingLabel({ idx, value: e.target.value })}
                            placeholder={ROLE_LABELS[sig.signature_role] || sig.signature_role}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { updateLabelOverride(idx, editingLabel.value); setEditingLabel({ idx: null, value: '' }); }
                              if (e.key === 'Escape') setEditingLabel({ idx: null, value: '' });
                            }}
                            data-testid={`ft-esign-label-input-${idx}`}
                          />
                          <button type="button" className="text-emerald-600 hover:text-emerald-700" onClick={() => { updateLabelOverride(idx, editingLabel.value); setEditingLabel({ idx: null, value: '' }); }}>
                            <Check className="w-4 h-4" />
                          </button>
                          <button type="button" className="text-slate-500 hover:text-slate-700" onClick={() => setEditingLabel({ idx: null, value: '' })}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <Badge variant="outline" className={`text-xs ${sig.display_label_override ? 'bg-amber-50 border-amber-300 text-amber-800' : ''}`}>
                            {roleDisplay(sig)}
                          </Badge>
                          <button type="button" className="text-slate-400 hover:text-blue-700" onClick={() => setEditingLabel({ idx, value: sig.display_label_override || '' })} title="Edit label on the signed PDF" data-testid={`ft-esign-label-edit-${idx}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                    {sig.removable ? (
                      <button type="button" className="text-red-500 hover:text-red-700" onClick={() => removeSignatory(idx)} title="Remove signatory" data-testid={`ft-esign-signatory-remove-${idx}`}>
                        <X className="w-4 h-4" />
                      </button>
                    ) : (
                      <span className="text-[10px] text-slate-400 uppercase tracking-wide" title="Auto-pulled — uncheck to exclude">Auto</span>
                    )}
                  </div>
                );
              })}

              <Button type="button" variant="outline" size="sm" className="w-full justify-center text-xs" onClick={openPicker} data-testid="ft-esign-add-signatory-btn">
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add Signatory
              </Button>
            </div>

            {/* Phase 5: step-2 summary of the configured review stages */}
            {showWizard && (
              <div className="border-t pt-3 flex items-center gap-2 text-xs text-slate-600 flex-wrap" data-testid="esign-wizard-stages-summary">
                <ListChecks className="w-3.5 h-3.5 shrink-0" />
                <span>
                  Review stages:{' '}
                  <span className="font-medium">
                    {reviewStages.length ? reviewStages.map(stageDisplay).join(' → ') : 'None — straight to e-signatures'}
                  </span>
                </span>
                <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setStep(1)} data-testid="esign-wizard-edit-stages-btn">
                  Edit
                </Button>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-4 flex items-center justify-between">
          {showWizard && step === 1 ? (
            <>
              <div className="text-sm text-gray-500">Step 1 of 2 — Review Stages</div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button variant="outline" onClick={() => { setReviewStages([]); setStep(2); }} data-testid="esign-wizard-skip-btn">
                  Skip — no review stages
                </Button>
                <Button onClick={() => setStep(2)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="esign-wizard-next-btn">
                  Next: Configure Signatures
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-sm text-gray-500">{checkedCount > 0 && `${checkedCount} document(s) checked`}</div>
              <div className="flex gap-2">
                {showWizard && (
                  <Button variant="outline" onClick={() => setStep(1)} disabled={submitting} data-testid="esign-wizard-back-btn">
                    Back
                  </Button>
                )}
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || loading || checkedCount === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="ft-esign-submit-btn"
                >
                  {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                  {submitting
                    ? (roundMode ? 'Starting…' : editMode ? 'Saving…' : 'Submitting…')
                    : (roundMode ? 'Start Round & Generate Bundle' : editMode ? 'Save Changes' : 'Approve & Generate Bundle')}
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Add Signatory Picker */}
      <Dialog open={pickerOpen} onOpenChange={(o) => { if (!o) { setPickerOpen(false); setPickerSearch(''); } }}>
        <DialogContent className="max-w-xl" data-testid="ft-esign-add-signatory-dialog">
          <DialogHeader>
            <DialogTitle>Add Signatory</DialogTitle>
            <DialogDescription>
              Pick a user to add to the bundle's signature list. Their role label appears on the signed PDF — edit it after adding with the pencil icon.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              autoFocus
              className="pl-9"
              placeholder="Search by name, email, or role…"
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
              data-testid="ft-esign-add-signatory-search"
            />
          </div>
          <Tabs defaultValue="involved" className="mt-2">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="involved" data-testid="ft-esign-picker-tab-involved">
                Involved in this request ({filterUsers(eligibleUsers.involved).length})
              </TabsTrigger>
              <TabsTrigger value="others" data-testid="ft-esign-picker-tab-others">
                All active users ({filterUsers(eligibleUsers.others).length})
              </TabsTrigger>
            </TabsList>
            {['involved', 'others'].map((tab) => {
              const list = filterUsers(tab === 'involved' ? eligibleUsers.involved : eligibleUsers.others);
              return (
                <TabsContent key={tab} value={tab}>
                  <div className="max-h-[40vh] overflow-y-auto space-y-1 mt-1">
                    {list.length === 0 ? (
                      <p className="text-sm text-slate-500 italic p-3 text-center">No matching users.</p>
                    ) : list.map(u => {
                      const outsideThread = !isUserInBundleThreads(u.user_id);
                      return (
                        <button
                          type="button"
                          key={u.user_id}
                          onClick={() => pickSignatory(u)}
                          className="w-full flex items-center justify-between p-2 rounded hover:bg-emerald-50 text-left border border-transparent hover:border-emerald-200 transition"
                          data-testid={`ft-esign-add-signatory-pick-${u.user_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate flex items-center gap-1.5">
                              {u.user_name}
                              {outsideThread && (
                                <span title="Not a member of any bundled document's discussion thread">
                                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 truncate">{u.email}</div>
                          </div>
                          <Badge variant="outline" className="text-[10px] flex-shrink-0 ml-2">
                            {ROLE_LABELS[u.user_role] || u.user_role}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPickerOpen(false); setPickerSearch(''); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm adding a user who isn't in any bundled document's thread */}
      <AlertDialog open={!!pendingAdd} onOpenChange={(o) => { if (!o) setPendingAdd(null); }}>
        <AlertDialogContent data-testid="ft-esign-outside-thread-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Add user outside the document threads?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{pendingAdd?.user_name}</strong> is not a member of any bundled document's discussion thread.
              They may not have seen these documents. Add them as a signatory anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ft-esign-outside-thread-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => { if (pendingAdd) addSignatory(pendingAdd); setPendingAdd(null); }}
              data-testid="ft-esign-outside-thread-confirm"
            >
              Add Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
